import React, { useState, useEffect, useRef } from 'react';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import Locate from '@arcgis/core/widgets/Locate';
import Search from '@arcgis/core/widgets/Search';

interface ArcGISMapProps {
  latitude: number;
  longitude: number;
  onLocationSelect: (_lat: number, _lng: number) => void;
}

const ArcGISMap: React.FC<ArcGISMapProps> = ({
  latitude,
  longitude,
  onLocationSelect,
}) => {
  const theme = document.documentElement.getAttribute("data-theme") ?? "light";
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  // const {id} = useParams<{id: string}>();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapViewRef = useRef<MapView | null>(null);
  const graphicsLayerRef = useRef<GraphicsLayer | null>(null);
  const markerGraphicRef = useRef<Graphic | null>(null);
  const searchRef = useRef<Search | null>(null);

  const [currentPosition, setCurrentPosition] = useState({
    lat: 0,
    lng: 20,
  });

  // handling location permission actions
  const handleEnableLocation = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ action: "OPEN_APP_SETTINGS" })
      );
    };
    setShowPermissionModal(false);
  };

  // Simple state to prevent multiple location requests
  // const [isRequestingLocation, setIsRequestingLocation] = useState(false);

  // useEffect(() => {
  //   if (!id && latitude === 0 && longitude === 0 && !isRequestingLocation) {
  //     console.log('Getting user current location...');
  //     getUserCurrentLocation();
  //   }
  // }, []);

  // Update currentPosition when props change
  useEffect(() => {
    if (latitude !== 0 || longitude !== 0) {
      setCurrentPosition({
        lat: latitude,
        lng: longitude,
      });
    }
  }, [latitude, longitude]);


  // Get user current location function (defined before use)
  // const getUserCurrentLocation = () => {
  //   // Prevent multiple simultaneous location requests
  //   if (isRequestingLocation) {
  //     return;
  //   }

  //   if (!navigator.geolocation) {
  //     console.log('Geolocation is not supported by this browser');
  //     return;
  //   }

  //   setIsRequestingLocation(true);

  //   navigator.geolocation.getCurrentPosition(
  //     position => {
  //       const lat = position.coords.latitude;
  //       const lng = position.coords.longitude;

  //       handleLocationSelect(lat, lng);
  //       setIsRequestingLocation(false);
  //     },
  //     error => {
  //       console.log('Location access denied or unavailable:', error.message);
  //       // Silently handle the error -, just log it
  //       setIsRequestingLocation(false);
  //     },
  //     {
  //       enableHighAccuracy: true,
  //       timeout: 10000,
  //       maximumAge: 60000,
  //     }
  //   );
  // };


  // Handle location selection
  const handleLocationSelect = (lat: number, lng: number) => {
    setCurrentPosition({ lat, lng });
    onLocationSelect(lat, lng);
  };

  // Initialize map
  useEffect(() => {
    // const hasValidCoords = currentPosition.lat !== 0 && currentPosition.lng !== 0;
    if (!mapRef.current) return;

    console.log('Initializing map...', { mapRef: mapRef.current });

    // Create web map with latest basemap
    const webmap = new WebMap({
      basemap: theme === 'light' ? 'streets-navigation-vector' : 'dark-gray-vector',
    });

    console.log(currentPosition, "curretPosition")
    // Create map view with modern configuration
    const view = new MapView({
      container: mapRef.current,
      map: webmap,
      constraints: { minZoom: 2 },
      center: [currentPosition.lng || 0, currentPosition.lat || 0],
      zoom: 12,
      popup: {
        dockEnabled: false,
        dockOptions: {
          buttonEnabled: false,
        },
        actions: [],
        defaultPopupTemplateEnabled: false,
      },
    });

    // Create graphics layer for markers
    const graphicsLayer = new GraphicsLayer();
    webmap.add(graphicsLayer);

    // Store references
    mapViewRef.current = view;
    graphicsLayerRef.current = graphicsLayer;

    // Configure popup
    if (view && view.popup) {
      view.popup.visibleElements = {
        heading: false,
        closeButton: false,
        actionBar: false,
      };
    }

    // Create individual containers
    const searchContainer = document.createElement('div');
    const locateContainer = document.createElement('div');
    // locateContainer.style.borderRadius = '26px';

    // Create Search widget with improved configuration
    const search = new Search({
      view: view,
      container: searchContainer,
      includeDefaultSources: false, // Hide dropdown/source selector
    });

    // Store search reference
    searchRef.current = search;

    // Configure search sources with improved performance settings
    // Using plain object format which is compatible with ArcGIS JS API
    const geocoderSource = {
    
      url: "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer",
      name: "ArcGIS World Geocoding Service",
      singleLineFieldName: "SingleLine",
      placeholder: "Search address",
      maxSuggestions: 20,
      outFields: ['*'],
      maxResults: 20,
      minSuggestCharacters: 2,
      exactMatch: false,
      popupEnabled: false,
      resultGraphicEnabled: true,
      showInMenu: false, // Don't show in dropdown menu
      countryCode: "ALL"
    } as any;

    // Set search sources
    search.sources = [geocoderSource];

    // Enable suggestions for default source
    if (search.viewModel && search.viewModel.defaultSources && 
        search.viewModel.defaultSources.length > 0) {
      const defaultSource = search.viewModel.defaultSources.getItemAt(0);
      if (defaultSource) {
        defaultSource.suggestionsEnabled = true;
      }
    }

    // Helper function to parse coordinates from search input
    const parseCoordinates = (input: string): { lat: number; lng: number } | null => {
      if (!input || typeof input !== 'string') return null;
      
      // Remove extra whitespace and common separators
      const cleaned = input.trim().replace(/[°'"]/g, '');
      
      // Try to match coordinate patterns
      // Pattern 1: "lat, lng" or "lng, lat" (comma separated)
      const commaPattern = /^(-?\d+\.?\d*)\s*[,，]\s*(-?\d+\.?\d*)$/;
      const commaMatch = cleaned.match(commaPattern);
      
      if (commaMatch) {
        const num1 = parseFloat(commaMatch[1]);
        const num2 = parseFloat(commaMatch[2]);
        
        // Determine which is lat and which is lng based on value ranges
        // Latitude: -90 to 90, Longitude: -180 to 180
        if (Math.abs(num1) <= 90 && Math.abs(num2) <= 180) {
          // num1 is likely lat, num2 is likely lng
          return { lat: num1, lng: num2 };
        } else if (Math.abs(num1) <= 180 && Math.abs(num2) <= 90) {
          // num1 is likely lng, num2 is likely lat
          return { lat: num2, lng: num1 };
        } else {
          // Default: assume first is lat, second is lng
          return { lat: num1, lng: num2 };
        }
      }
      
      // Pattern 2: "lat lng" or "lng lat" (space separated)
      const spacePattern = /^(-?\d+\.?\d*)\s+(-?\d+\.?\d*)$/;
      const spaceMatch = cleaned.match(spacePattern);
      
      if (spaceMatch) {
        const num1 = parseFloat(spaceMatch[1]);
        const num2 = parseFloat(spaceMatch[2]);
        
        // Determine which is lat and which is lng
        if (Math.abs(num1) <= 90 && Math.abs(num2) <= 180) {
          return { lat: num1, lng: num2 };
        } else if (Math.abs(num1) <= 180 && Math.abs(num2) <= 90) {
          return { lat: num2, lng: num1 };
        } else {
          return { lat: num1, lng: num2 };
        }
      }
      
      return null;
    };

    // Handle search completion - navigate to selected location
    search.on('search-complete', async (event) => {
      // Check if the search term is coordinates first
      const searchTerm = event.searchTerm || '';
      const coords = parseCoordinates(searchTerm);
      
      // If coordinates are detected, use them directly
      if (coords && !isNaN(coords.lat) && !isNaN(coords.lng) &&
          coords.lat >= -90 && coords.lat <= 90 &&
          coords.lng >= -180 && coords.lng <= 180) {
        console.log('Coordinate search detected:', coords);
        handleLocationSelect(coords.lat, coords.lng);
        
        // Navigate map to the coordinates
        if (mapViewRef.current) {
          mapViewRef.current.goTo({
            center: [coords.lng, coords.lat],
            zoom: 15,
            duration: 1000
          }).catch(error => {
            console.log('Map navigation error:', error);
          });
        }
        return; // Skip geocoder results, use coordinates directly
      }
      
      let results = event.results || [];
      
      // Filter out empty results
      results = results.filter((sr: any) => sr.results?.length);
      
      if (results.length === 0) return;
      
      // Separate ArcGIS Geocoder results from custom layer results
      const geocoderResults = results.filter((r: any) =>
        r.source?.name?.toLowerCase().includes("arcgis world geocoding service")
      );
      
      const customLayerResults = results.filter((r: any) =>
        r.source && 'layer' in r.source && r.source.layer && r.source.layer.type === "feature"
      );
      
      // Combine both result sets and extract all individual results
      const allResults: any[] = [];
      
      // Extract results from geocoder
      geocoderResults.forEach((resultGroup: any) => {
        if (resultGroup.results && resultGroup.results.length > 0) {
          resultGroup.results.forEach((result: any) => {
            allResults.push({ ...result, sourceType: 'geocoder', sourceGroup: resultGroup });
          });
        }
      });
      
      // Extract results from custom layers
      customLayerResults.forEach((resultGroup: any) => {
        if (resultGroup.results && resultGroup.results.length > 0) {
          resultGroup.results.forEach((result: any) => {
            allResults.push({ ...result, sourceType: 'custom', sourceGroup: resultGroup });
          });
        }
      });
      
      // Filter duplicates based on coordinates (within a small tolerance)
      const uniqueResults: any[] = [];
      const seenCoordinates = new Set<string>();
      const COORDINATE_TOLERANCE = 0.0001; // Approximately 11 meters
      
      allResults.forEach((result: any) => {
        const geometry = result.feature?.geometry;
        if (!geometry) return;
        
        let lat: number | null = null;
        let lng: number | null = null;
        
        // Extract coordinates
        if (geometry.type === 'point') {
          const point = geometry as Point;
          if (point.latitude !== null && point.latitude !== undefined &&
              point.longitude !== null && point.longitude !== undefined) {
            lat = point.latitude;
            lng = point.longitude;
          } else if (point.x !== null && point.x !== undefined &&
                     point.y !== null && point.y !== undefined) {
            if (point.spatialReference?.wkid === 4326 || !point.spatialReference) {
              lat = point.y;
              lng = point.x;
            }
          }
        } else if ('extent' in geometry) {
          const extent = (geometry as any).extent;
          if (extent) {
            lat = (extent.ymin + extent.ymax) / 2;
            lng = (extent.xmin + extent.xmax) / 2;
          }
        }
        
        if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
          // Round coordinates to tolerance level for duplicate detection
          const roundedLat = Math.round(lat / COORDINATE_TOLERANCE) * COORDINATE_TOLERANCE;
          const roundedLng = Math.round(lng / COORDINATE_TOLERANCE) * COORDINATE_TOLERANCE;
          const coordKey = `${roundedLat.toFixed(4)},${roundedLng.toFixed(4)}`;
          
          if (!seenCoordinates.has(coordKey)) {
            seenCoordinates.add(coordKey);
            uniqueResults.push(result);
          }
        } else {
          // If we can't extract coordinates, add it anyway (no duplicate check)
          uniqueResults.push(result);
        }
      });
      
      // Sort results: prefer geocoder results first, then custom layer results
      uniqueResults.sort((a, b) => {
        if (a.sourceType === 'geocoder' && b.sourceType === 'custom') return -1;
        if (a.sourceType === 'custom' && b.sourceType === 'geocoder') return 1;
        return 0;
      });
      
      // Process the first result
      if (uniqueResults.length > 0) {
        const firstResult = uniqueResults[0];
        const geometry = firstResult.feature?.geometry;
        
        if (geometry) {
          let lat: number | null = null;
          let lng: number | null = null;
          
          // Handle Point geometry - extract coordinates
          if (geometry.type === 'point') {
            const point = geometry as Point;
            // Try latitude/longitude first (geographic coordinates)
            if (point.latitude !== null && point.latitude !== undefined &&
                point.longitude !== null && point.longitude !== undefined) {
              lat = point.latitude;
              lng = point.longitude;
            } else if (point.x !== null && point.x !== undefined &&
                       point.y !== null && point.y !== undefined) {
              // For x/y coordinates, check spatial reference
              // If it's WGS84 (4326), y is latitude and x is longitude
              if (point.spatialReference?.wkid === 4326 || !point.spatialReference) {
                lat = point.y;
                lng = point.x;
              } else if (mapViewRef.current) {
                // Convert from map's spatial reference to geographic
                const geographicPoint = point.clone();
                // Use the view's spatial reference for conversion if needed
                lat = geographicPoint.y ?? null;
                lng = geographicPoint.x ?? null;
              }
            }
          } else if ('extent' in geometry) {
            // Handle extent - use center
            const extent = (geometry as any).extent;
            if (extent) {
              lat = (extent.ymin + extent.ymax) / 2;
              lng = (extent.xmin + extent.xmax) / 2;
            }
          }
          
          if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
            console.log('Search result selected:', { lat, lng });
            handleLocationSelect(lat, lng);
            
            // Navigate map to the selected location
            if (mapViewRef.current) {
              mapViewRef.current.goTo({
                center: [lng, lat],
                zoom: 15,
                duration: 1000
              }).catch(error => {
                console.log('Map navigation error:', error);
              });
            }
          }
        }
      }
    });

    //Handle search result click
    search.on('select-result', () => {
      view.graphics.removeAll();
    });

    // Handle suggestion completion - improve suggestion performance
    search.on('suggest-complete', (event) => {
      // Suggestions are automatically handled by the search widget
      // This event can be used for additional customization if needed
      const suggestions = event.results || [];
      console.log('Search suggestions:', suggestions.length);
    });

    // Handle suggestion start
    search.on('suggest-start', () => {
      // Enable/disable suggestions based on active source
      enabledDisabledSuggestions();
    });

    // Function to enable/disable suggestions based on active source
    const enabledDisabledSuggestions = () => {
      if (!searchRef.current || !searchRef.current.viewModel) return;
      
      const activeSource = searchRef.current.viewModel.activeSource;
      // Check if active source is a layer source (has layer property)
      const isCustomSource = activeSource && 'layer' in activeSource && 
                             (activeSource as any).layer && 
                             (activeSource as any).layer.type === "feature";
      
      if (searchRef.current.viewModel.defaultSources && 
          searchRef.current.viewModel.defaultSources.length > 0) {
        const defaultSource = searchRef.current.viewModel.defaultSources.getItemAt(0);
        if (defaultSource) {
          if (isCustomSource) {
            defaultSource.suggestionsEnabled = false;
          } else {
            defaultSource.suggestionsEnabled = true;
          }
        }
      }
    };

    // Enable user location button
    const locateBtn = new Locate({
      view: view,
      graphic: null as any,
      container: locateContainer,
    });

    locateBtn.on('locate', event => {
      console.log('Locate widget found location:', event);

      if (event.position) {
        const lat = event.position.coords.latitude;
        const lng = event.position.coords.longitude;
        console.log('Locate widget found location:', { lat, lng });
        handleLocationSelect(lat, lng);
        view.graphics.removeAll();
      }
    });

    // Handle locate widget errors silently
    locateBtn.on('locate-error', (event) => {
      setShowPermissionModal(true);
      console.log('Locate widget error:', event.error?.message || 'Location access denied');
      // Silently handle the error - no popup
    });

    // view.ui.add(locateBtn, 'top-right');

    const widgetWrapper = document.createElement('div');
    widgetWrapper.style.display = 'flex';
    widgetWrapper.style.alignItems = 'center';
    widgetWrapper.style.gap = '0.25rem';

    widgetWrapper.appendChild(searchContainer);
    widgetWrapper.appendChild(locateContainer);

    view.ui.add(widgetWrapper, 'top-right');

    // // Wait for view to be ready before adding events
    // view
    //   .when(() => {
    //     console.log('Map view is ready');

    //     // Add click event to map
    //     view.on('click', event => {
    //       const point = view.toMap(event);
    //       const lat = point.latitude || 0;
    //       const lng = point.longitude || 0;

    //       console.log('Map clicked at:', { lat, lng });
    //       handleLocationSelect(lat, lng);
    //     });

    //     // Add drag event
    //     view.on('drag', event => {
    //       if (!markerGraphicRef.current) return;

    //       event.stopPropagation();

    //       if (event.action === 'update') {
    //         // while dragging, update marker position visually
    //         const point = view.toMap({ x: event.x, y: event.y });
    //         markerGraphicRef.current.geometry = point;
    //       }

    //       if (event.action === 'end') {
    //         // drag ended → finalize selection
    //         const point = markerGraphicRef.current.geometry as Point;
    //         if (point) {
    //           console.log('Marker dragged to:', {
    //             lat: point.latitude,
    //             lng: point.longitude,
    //           });
    //           handleLocationSelect(point.latitude ?? 0, point.longitude ?? 0);
    //         }
    //       }
    //     });
    //   })
    //   .catch(error => {
    //     console.error('Error initializing map view:', error);
    //   });

    // Wait for view to be ready before adding events
    view
      .when(() => {
        console.log('Map view is ready');

        let selectedForDrag = false; // flag to allow dragging only if marker clicked

        // Add click event to map
        view.on('click', event => {
          view.hitTest(event).then(response => {
            // check if clicked on the marker
            const markerHit = response.results.find(
              (r: any) => r.graphic === markerGraphicRef.current
            );

            if (markerHit) {
              // marker clicked → enable dragging
              selectedForDrag = true;
              console.log('Marker selected for dragging');
            } else {
              // clicked elsewhere → disable dragging
              selectedForDrag = false;

              const point = view.toMap(event);
              const lat = point.latitude || 0;
              const lng = point.longitude || 0;

              console.log('Map clicked at:', { lat, lng });
              // If you *don't* want to move marker here, remove this line:
              handleLocationSelect(lat, lng);
            }
          });
        });

        // Add drag event
        view.on('drag', event => {
          if (!markerGraphicRef.current || !selectedForDrag) return;

          event.stopPropagation(); // prevent map panning while dragging

          if (event.action === 'update') {
            // while dragging, update marker position visually
            const point = view.toMap({ x: event.x, y: event.y });
            markerGraphicRef.current.geometry = point;
          }

          if (event.action === 'end') {
            // drag ended → finalize selection
            const point = markerGraphicRef.current.geometry as Point;
            if (point) {
              console.log('Marker dragged to:', {
                lat: point.latitude,
                lng: point.longitude,
              });
              handleLocationSelect(point.latitude ?? 0, point.longitude ?? 0);
            }
            selectedForDrag = false; // reset after drag ends
          }
        });
      })
      .catch(error => {
        console.error('Error initializing map view:', error);
      });

    // Cleanup
    return () => {
      if (searchRef.current) {
        searchRef.current.destroy();
        searchRef.current = null;
      }
      if (mapViewRef.current) {
        mapViewRef.current.destroy();
        mapViewRef.current = null;
      }
      if (graphicsLayerRef.current) {
        graphicsLayerRef.current.destroy();
        graphicsLayerRef.current = null;
      }
    };
  }, []);

  

  // Update marker when currentPosition changes
  const createMarker = () => {
    setTimeout(() => {
      if (
        !graphicsLayerRef.current ||
        !mapViewRef.current ||
        !mapViewRef.current.ready
      )
        return;

      // Remove existing marker
      if (markerGraphicRef.current) {
        graphicsLayerRef.current.remove(markerGraphicRef.current);
      }

      // Create new marker - ensure valid coordinates
      if (currentPosition.lat === 0 && currentPosition.lng === 0) {
        console.log('Skipping marker creation - coordinates are 0,0');
        return;
      }

      const point = new Point({
        longitude: currentPosition.lng,
        latitude: currentPosition.lat,
      });

      const markerGraphic = new Graphic({
        geometry: point,
        symbol: {
          type: 'picture-marker',
          url: `${process.env.PUBLIC_URL}/SVGIcons/MapIcon.svg`,
          width: '25px',
          height: '30px',
        },
        attributes: {
          title: 'Your Location',
          lat: currentPosition.lat,
          lng: currentPosition.lng,
        },
        popupTemplate: {},
      });

      // Add marker to graphics layer
      graphicsLayerRef.current.add(markerGraphic);
      markerGraphicRef.current = markerGraphic;

      // Center map on marker - only if view is ready
      if (mapViewRef.current && mapViewRef.current.ready) {
        mapViewRef.current
          .goTo({
            target: point,
            duration: 1000,
          })
          .catch(error => {
            console.log('Map navigation error:', error);
          });
      }
    }, 1000);
  };
  
  useEffect(() => {
    createMarker();
  }, [currentPosition]);

  return (
    <>
     <div className="map-view w-full h-full overflow-hidden" ref={mapRef}></div>

      {/* Location Permission Denied Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[70] bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-md w-full relative overflow-hidden">
            {/* Close Button */}
            <button
              onClick={() => setShowPermissionModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                <path d="M18 6L6 18M6 6l12 12"></path>
              </svg>
            </button>

            <div className="p-8 flex flex-col items-center text-center">
              {/* Location Blocked Icon */}
              <div className="mb-6">
                <img
                  src={`${process.env.PUBLIC_URL}/SVGIcons/LocationDenied.svg`}
                  alt="Location Blocked"
                  style={{ width: '96px', height: '96px' }}
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-800 mb-3">Location Access Required</h2>
              <p className="text-gray-600 text-sm mb-2 max-w-sm leading-relaxed">
                To add device location, please enable location access:
              </p>
              <ul className="text-left text-gray-600 text-sm mb-6 space-y-1 max-w-sm">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Browser:</strong> Click the location icon in the address bar and allow access</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Mobile:</strong> Go to Settings → Apps → SmartEco → Permissions</span>
                </li>
              </ul>
              <p className="text-gray-500 text-xs mb-6 italic">
                After enabling location, return here to continue adding your device.
              </p>

              <button
                onClick={handleEnableLocation}
                className="w-full max-w-xs text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                style={{
                  background: 'linear-gradient(to right, #0F796B, #6FDC95)'
                }}
              >
                Go to App Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </>


  );
};

export default ArcGISMap;
